import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { userAPI } from '../services/api';
import LoadingIndicator from '../components/LoadingIndicator';
import './Profile.css';

const emptyEmergency = { name: '', phone: '', relation: '' };
const RELATION_OPTIONS = ['Parent', 'Spouse', 'Sibling', 'Friend', 'Other'];
const FITNESS_OPTIONS = [
  'Beginner (New to fitness)',
  'Intermediate (Regular exercise)',
  'Advanced (Very active)'
];

const formatDateForInput = (dateStr) => {
  if (!dateStr) return '';
  const d = dateStr.split('T')[0];
  return d;
};

const formatDateForDisplay = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('T')[0].split('-');
  return `${m}/${d}/${y}`;
};

const isValidEmailFormat = (value) => {
  const s = String(value || '').trim();
  if (!s) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
};

const formatAccountPhoneDisplay = (phone) => {
  if (!phone) return '—';
  const d = String(phone).replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('91')) return `+91 ${d.slice(2, 7)} ${d.slice(7)}`;
  if (d.length === 11 && d.startsWith('0')) return `${d.slice(0, 5)} ${d.slice(5)}`;
  if (d.length === 10) return `+91 ${d.slice(0, 5)} ${d.slice(5)}`;
  return String(phone).trim();
};

const formatEmergencyPhoneInput = (raw) => {
  const digits = String(raw || '').replace(/\D/g, '').slice(0, 12);
  if (!digits) return '';

  // Prefer +91 formatting when the number includes country code.
  if (digits.length > 10 && digits.startsWith('91')) {
    const local = digits.slice(2, 12);
    if (!local) return '+91';
    if (local.length <= 5) return `+91 ${local}`;
    return `+91 ${local.slice(0, 5)} ${local.slice(5)}`;
  }

  // Local mobile formatting: 98765 43210
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)} ${digits.slice(5, 10)}`;
};

const isEmergencyPhoneValid = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return true; // optional field
  if (digits.length === 10) return true;
  return digits.length === 12 && digits.startsWith('91');
};

const getTodayInputDate = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const Profile = () => {
  const { user: contextUser, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    dateOfBirth: '',
    gender: '',
    bloodGroup: '',
    physicalFitnessLevel: '',
    allergies: '',
    existingMedicalConditions: '',
    emergencyContact: { ...emptyEmergency },
    profileImageUrl: '',
    medicalCertificateUrl: '',
    medicalCertificateUploadedAt: ''
  });
  const [avatarPreview, setAvatarPreview] = useState(null);
  const profileFileRef = useRef(null);
  const medicalFileRef = useRef(null);
  const maxBirthDate = getTodayInputDate();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await userAPI.getProfile();
        const user = res.data.data?.user || res.data.user;
        setProfile(user);
        const ec = user?.emergencyContact && typeof user.emergencyContact === 'object' ? user.emergencyContact : emptyEmergency;
        setFormData({
          fullName: [user?.firstName, user?.lastName].filter(Boolean).join(' '),
          email: user?.email ?? '',
          dateOfBirth: formatDateForInput(user?.dateOfBirth) ?? '',
          gender: user?.gender ?? '',
          bloodGroup: user?.bloodGroup ?? '',
          physicalFitnessLevel: user?.physicalFitnessLevel ?? '',
          allergies: user?.allergies ?? '',
          existingMedicalConditions: user?.existingMedicalConditions ?? '',
          emergencyContact: { ...emptyEmergency, ...ec },
          profileImageUrl: user?.profileImageUrl ?? '',
          medicalCertificateUrl: user?.medicalCertificateUrl ?? '',
          medicalCertificateUploadedAt: user?.medicalCertificateUploadedAt ?? ''
        });
        if (user?.profileImageUrl) setAvatarPreview(user.profileImageUrl);
      } catch (e) {
        setMessage({ type: 'error', text: 'Failed to load profile' });
      } finally {
        setLoading(false);
      }
    };
    if (contextUser) load();
    else setLoading(false);
  }, [contextUser?.id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'dateOfBirth' && value && value > maxBirthDate) {
      setMessage({ type: 'error', text: 'Birthdate cannot be in the future.' });
      return;
    }
    if (name === 'dateOfBirth') {
      setMessage((prev) => (prev.text === 'Birthdate cannot be in the future.' ? { type: '', text: '' } : prev));
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEmergencyChange = (field, value) => {
    const nextValue = field === 'phone' ? formatEmergencyPhoneInput(value) : value;
    setFormData((prev) => ({
      ...prev,
      emergencyContact: { ...prev.emergencyContact, [field]: nextValue }
    }));
  };

  const onProfileImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(jpeg|jpg|png|gif)$/i.test(file.name)) {
      setMessage({ type: 'error', text: 'Allowed: JPEG, JPG, PNG, GIF. Max 3 MB.' });
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Profile image must be under 3 MB' });
      return;
    }
    setMessage({ type: '', text: '' });
    setUploadingAvatar(true);
    try {
      const localPreview = URL.createObjectURL(file);
      setAvatarPreview(localPreview);
      const res = await userAPI.uploadProfileImage(file);
      const uploadedUrl = res?.data?.data?.url || '';
      if (!uploadedUrl) throw new Error('Upload succeeded but no URL was returned');

      setFormData((prev) => ({ ...prev, profileImageUrl: uploadedUrl }));
      setAvatarPreview(uploadedUrl);
      setMessage({ type: 'success', text: 'Photo uploaded. Click Save Changes to persist it.' });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err?.response?.data?.message || 'Failed to upload profile image'
      });
    } finally {
      setUploadingAvatar(false);
      if (profileFileRef.current) profileFileRef.current.value = '';
    }
  };

  const onMedicalCertChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setMessage({ type: '', text: '' });
    // TODO: upload file and set medicalCertificateUrl, medicalCertificateUploadedAt when API is ready
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    const trimmedEmail = (formData.email || '').trim();
    if (trimmedEmail && !isValidEmailFormat(trimmedEmail)) {
      setMessage({ type: 'error', text: 'Please enter a valid email address (e.g. name@example.com).' });
      return;
    }
    if (!isEmergencyPhoneValid(formData.emergencyContact.phone)) {
      setMessage({ type: 'error', text: 'Emergency phone must be a valid 10-digit mobile number.' });
      return;
    }
    if (formData.dateOfBirth && formData.dateOfBirth > maxBirthDate) {
      setMessage({ type: 'error', text: 'Birthdate cannot be in the future.' });
      return;
    }
    setSaving(true);
    try {
      const parts = (formData.fullName || '').trim().split(/\s+/);
      const firstName = parts[0] || '';
      const lastName = parts.slice(1).join(' ') || '';
      const payload = {
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        email: trimmedEmail || null,
        dateOfBirth: formData.dateOfBirth || null,
        gender: formData.gender || null,
        bloodGroup: formData.bloodGroup || null,
        physicalFitnessLevel: formData.physicalFitnessLevel || null,
        allergies: formData.allergies || null,
        existingMedicalConditions: formData.existingMedicalConditions || null,
        profileImageUrl: formData.profileImageUrl || null,
        medicalCertificateUrl: formData.medicalCertificateUrl || null,
        medicalCertificateUploadedAt: formData.medicalCertificateUploadedAt || null,
        emergencyContact: (formData.emergencyContact.name || formData.emergencyContact.phone) ? formData.emergencyContact : null
      };
      const updateRes = await userAPI.updateProfile(payload);
      const updatedUser = updateRes?.data?.data?.user || null;
      if (updatedUser) {
        setProfile(updatedUser);
        const ec = updatedUser?.emergencyContact && typeof updatedUser.emergencyContact === 'object'
          ? updatedUser.emergencyContact
          : formData.emergencyContact;
        setFormData((prev) => ({
          ...prev,
          fullName: [updatedUser?.firstName, updatedUser?.lastName].filter(Boolean).join(' ') || prev.fullName,
          email: String(updatedUser?.email ?? '').trim() || prev.email,
          dateOfBirth: formatDateForInput(updatedUser?.dateOfBirth) || prev.dateOfBirth,
          gender: updatedUser?.gender ?? prev.gender,
          bloodGroup: updatedUser?.bloodGroup ?? prev.bloodGroup,
          physicalFitnessLevel: String(updatedUser?.physicalFitnessLevel ?? '').trim() || prev.physicalFitnessLevel,
          allergies: updatedUser?.allergies ?? prev.allergies,
          existingMedicalConditions: updatedUser?.existingMedicalConditions ?? prev.existingMedicalConditions,
          emergencyContact: { ...emptyEmergency, ...ec },
          profileImageUrl: updatedUser?.profileImageUrl ?? prev.profileImageUrl,
          medicalCertificateUrl: updatedUser?.medicalCertificateUrl ?? prev.medicalCertificateUrl,
          medicalCertificateUploadedAt: updatedUser?.medicalCertificateUploadedAt ?? prev.medicalCertificateUploadedAt
        }));
      }
      // Keep auth context in sync without reloading this screen's form state.
      if (typeof refreshUser === 'function') refreshUser();
      setMessage({ type: 'success', text: 'Profile updated successfully' });
    } catch (err) {
      const apiErrors = err.response?.data?.errors;
      const firstErr =
        Array.isArray(apiErrors) && apiErrors.length > 0
          ? apiErrors[0].msg || apiErrors[0].message
          : null;
      setMessage({
        type: 'error',
        text: firstErr || err.response?.data?.message || 'Failed to update profile'
      });
    } finally {
      setSaving(false);
    }
  };

  const user = profile || contextUser;
  const accountPhone = user?.phone ?? '';
  const nameParts = user ? [user.firstName, user.lastName].filter(Boolean).join(' ') : '';
  const displayName = nameParts.trim() || 'User';

  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-page-header">
          <h1 className="profile-page-title">Profile Settings</h1>
          <div className="profile-page-subtitle">
            <LoadingIndicator label="Loading..." />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-page-header">
        <h1 className="profile-page-title">Profile Settings</h1>
      </div>

      {message.text && (
        <div className={`profile-message ${message.type}`}>{message.text}</div>
      )}

      <form onSubmit={handleSubmit} className="profile-form">
        {/* Card 1: Personal Information */}
        <section className="profile-card profile-card-rounded">
          <h2 className="profile-card-title-v2">Personal Information</h2>
          <div className="profile-card-body profile-personal-layout">
            <div className="profile-personal-left">
              <div className="profile-grid-2">
                <div className="form-group">
                  <label htmlFor="fullName">Full Name</label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="e.g. Rahul Sharma"
                    className="profile-input-rounded"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    className="profile-input-rounded"
                    inputMode="email"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="text"
                    id="phone"
                    readOnly
                    tabIndex={-1}
                    value={formatAccountPhoneDisplay(accountPhone)}
                    aria-readonly="true"
                    title="Login number — contact support to change"
                    className="profile-input-rounded input-readonly"
                  />
                  <p className="profile-field-hint">This number is used to log in and cannot be edited here.</p>
                </div>
                <div className="form-group">
                  <label htmlFor="dateOfBirth">Birthdate</label>
                  <input
                    type="date"
                    id="dateOfBirth"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    max={maxBirthDate}
                    className="profile-input-rounded"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="gender">Gender</label>
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className="profile-input-rounded"
                  >
                    <option value="">Select</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="profile-personal-right">
              <div className="profile-picture-upload-v2">
                <div
                  className="profile-avatar-large-v2"
                  onClick={() => profileFileRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && profileFileRef.current?.click()}
                >
                  {avatarPreview ? (
                    <img src={avatarPreview} alt="" />
                  ) : (
                    <span>{displayName.charAt(0).toUpperCase()}</span>
                  )}
                  <div className="profile-avatar-overlay">
                    <span className="material-symbols-outlined">photo_camera</span>
                  </div>
                  <button
                    type="button"
                    className="profile-avatar-edit-btn"
                    onClick={() => profileFileRef.current?.click()}
                  >
                    <span className="material-symbols-outlined">edit</span>
                  </button>
                </div>
                <input
                  ref={profileFileRef}
                  type="file"
                  accept=".jpeg,.jpg,.png,.gif"
                  className="hidden-input"
                  onChange={onProfileImageChange}
                />
                <p className="profile-upload-hint-v2">
                  Allowed *.jpeg, *.jpg, *.png, *.gif
                  <br />
                  max size of 3 MB
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Card 2: Medical & Health Profile */}
        <section className="profile-card profile-card-rounded">
          <h2 className="profile-card-title-v2">Medical &amp; Health Profile</h2>
          <div className="profile-card-body">
            <div className="profile-grid-2">
              <div className="form-group">
                <label htmlFor="bloodGroup">Blood Group</label>
                <select
                  id="bloodGroup"
                  name="bloodGroup"
                  value={formData.bloodGroup}
                  onChange={handleChange}
                  className="profile-input-rounded"
                >
                  <option value="">Select</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="physicalFitnessLevel">Physical Fitness Level</label>
                <select
                  id="physicalFitnessLevel"
                  name="physicalFitnessLevel"
                  value={formData.physicalFitnessLevel}
                  onChange={handleChange}
                  className="profile-input-rounded"
                >
                  <option value="">Select</option>
                  {FITNESS_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group form-group-full">
                <label htmlFor="allergies">Allergies</label>
                <input
                  type="text"
                  id="allergies"
                  name="allergies"
                  value={formData.allergies}
                  onChange={handleChange}
                  placeholder="e.g. Peanuts, Dust (Leave blank if none)"
                  className="profile-input-rounded"
                />
              </div>
              <div className="form-group form-group-full">
                <label htmlFor="existingMedicalConditions">Existing Medical Conditions</label>
                <textarea
                  id="existingMedicalConditions"
                  name="existingMedicalConditions"
                  value={formData.existingMedicalConditions}
                  onChange={handleChange}
                  placeholder="e.g. Asthma, Diabetes (Leave blank if none)"
                  rows={2}
                  className="profile-input-rounded profile-textarea"
                />
              </div>
            </div>

            <div className="medical-cert-upload medical-cert-upload-v2">
              <label className="profile-medical-label">Medical Certificate</label>
              <div className="medical-cert-inner">
                <div
                  className="medical-cert-zone medical-cert-zone-v2"
                  onClick={() => medicalFileRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && medicalFileRef.current?.click()}
                >
                  <div className="medical-cert-icon-wrap">
                    <svg
                      className="medical-cert-icon-svg"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        d="M7 2a2 2 0 0 0-2 2v16a1 1 0 0 0 1.447.894L12 19.118l5.553 1.776A1 1 0 0 0 19 20V4a2 2 0 0 0-2-2H7z"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M9 7h6M9 11h4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                  <div className="medical-cert-text">
                    <p className="medical-cert-title">Click to upload or drag and drop</p>
                    <p className="medical-cert-sub">PDF, JPG or PNG (max. 5MB)</p>
                    <button type="button" className="btn-upload-cert-v2">
                      Upload Certificate
                    </button>
                  </div>
                </div>
                <input
                  ref={medicalFileRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.pdf"
                  className="hidden-input"
                  onChange={onMedicalCertChange}
                />
                {formData.medicalCertificateUploadedAt && (
                  <p className="medical-cert-last-v2">
                    <svg
                      className="medical-cert-history-icon"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        d="M4 4v5h5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M5 13a7 7 0 1 0 2-5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 8v4l2.5 1.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Last uploaded on: {formatDateForDisplay(formData.medicalCertificateUploadedAt)}
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Card 3: Emergency Contact */}
        <section className="profile-card profile-card-rounded">
          <h2 className="profile-card-title-v2">Emergency Contact</h2>
          <div className="profile-card-body">
            <div className="profile-grid-3">
              <div className="form-group">
                <label>Contact Name</label>
                <input
                  type="text"
                  value={formData.emergencyContact.name}
                  onChange={(e) => handleEmergencyChange('name', e.target.value)}
                  placeholder="Guardian/Parent Name"
                  className="profile-input-rounded"
                />
              </div>
              <div className="form-group">
                <label>Relationship</label>
                <select
                  value={formData.emergencyContact.relation}
                  onChange={(e) => handleEmergencyChange('relation', e.target.value)}
                  className="profile-input-rounded"
                >
                  <option value="">Select</option>
                  {RELATION_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Emergency Phone Number</label>
                <input
                  type="tel"
                  value={formData.emergencyContact.phone}
                  onChange={(e) => handleEmergencyChange('phone', e.target.value)}
                  placeholder="+91"
                  className="profile-input-rounded"
                />
              </div>
            </div>
          </div>
        </section>

        <div className="profile-form-actions profile-form-actions-v2">
          <button type="submit" className="btn-primary-v2" disabled={saving || uploadingAvatar}>
            {uploadingAvatar ? 'Uploading Photo...' : saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Profile;
